import { useMemo } from "react";
import { useDispatch } from "react-redux";
import Button from "react-bootstrap/Button";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { ConfirmModal } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/confirm-modal/confirm-modal";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { OrganizationConfig } from "@idw-types/data/organization-config";
import { AxnIdentityProvider } from "@idw-types/data/reference-data/axn";
import { setOrganizationDetails } from "@redux/actions/organization-details-actions";
import { configureCorporateLoginRequest } from "@requests/service-configuration/configure-corporate-login";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import styles from "./organization-details-admin-login-settings-tab.less";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import Alert from "react-bootstrap/esm/Alert";
import { IdentityProviderDetailsReferenceData } from "@idw-types/state/identity-provider-details-state";
import { AddIdentityProviderOrgModal } from "@containers/modals/add-identity-provider-org-modal/add-identity-provider-modal-org";
import { sortStrings } from "@utils/string-util";

type OrganizationDetailsAdminLoginSettingsTabProps = {
    isDisabled: boolean;
    organizationConfigState: OrganizationConfig;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    serviceName: string;
    serviceApiKey: string;
    shouldShowIDPIDWarning: boolean;
    canCreateIdentityProviders: boolean;
    identityProviderReferenceData: IdentityProviderDetailsReferenceData;
    setOrganizationConfigState: (organizationConfigState: OrganizationConfig) => void;
    onNewIdpCreated: (identityProvider: AxnIdentityProvider) => void;
};

export const OrganizationDetailsAdminLoginSettingsTab = (props: OrganizationDetailsAdminLoginSettingsTabProps) => {
    const {
        isDisabled,
        organizationConfigState,
        axnIdentityProviders,
        serviceName,
        serviceApiKey,
        shouldShowIDPIDWarning,
        identityProviderReferenceData,
        canCreateIdentityProviders,
        onNewIdpCreated,
        setOrganizationConfigState
    } = props;

    const modalContext = useModalContext();
    const dispatch = useDispatch();
    const labelWidth = LabelWidthTypes.MEDIUM;

    const handleCreateCorporateLogin = async () => {
        const newCorporateLogin = await configureCorporateLoginRequest(organizationConfigState.id);
        if (newCorporateLogin?.data) {
            dispatch(setOrganizationDetails(newCorporateLogin.data));

            displaySuccessModal(
                dispatch,
                resources.requests.configureCorporateLogin.success.title,
                resources.requests.configureCorporateLogin.success.message
            );
        } else {
            if (newCorporateLogin?.error) {
                logAndDisplayErrorModal(
                    dispatch,
                    newCorporateLogin.error,
                    resources.requests.configureCorporateLogin.error.title,
                    resources.requests.configureCorporateLogin.error.message,
                    null,
                    null,
                    newCorporateLogin.body
                );
            }
        }
    };

    const axnIdentityProviderOptions = useMemo(() => {
        return axnIdentityProviders.map(idp => {
            return {
                label: idp.name,
                value: idp.id
            }
        }).sort((a, b) => sortStrings(a.label, b.label));
    }, [axnIdentityProviders]);

    const selectedIdentityProviderOption = useMemo(() => {
        const { loginServiceIDPID } = organizationConfigState;
        if (loginServiceIDPID) {
            return axnIdentityProviderOptions.find(idpOption => idpOption.value === loginServiceIDPID) || null;
        }

        return null;
    }, [axnIdentityProviderOptions, organizationConfigState.loginServiceIDPID]);

    const handleConfirmCreateCorportateLogin = async () => {
        const renderModal = (onModalClose: () => void) => {

            return (
                <ConfirmModal
                    id={"confirm-create-corporate-login"}
                    title={"Create Corporate Login"}
                    confirmMessage={"You are able to create a corporate login. Do you want to continue?"}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={resources.common.buttons.continue.label}
                    onSubmit={null}
                    onSubmitAsync={handleCreateCorporateLogin}
                    onModalClose={onModalClose}
                    customProps={null}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    }

    const handleIdpChange = (selectedOption: SelectOption<string>) => {
        setOrganizationConfigState({
            ...organizationConfigState,
            loginServiceIDPID: selectedOption.value
        });
    }

    const handleIdpCreate = (identityProvider: AxnIdentityProvider) => {
        onNewIdpCreated(identityProvider);
        setOrganizationConfigState({
            ...organizationConfigState,
            loginServiceIDPID: identityProvider.id
        });
    }

    const handleOpenCreateIDPModal = () => {
        const renderModal = (onModalClose: () => void) => {

            return (
                <AddIdentityProviderOrgModal
                    identityProviderReferenceData={identityProviderReferenceData}
                    organizationId={organizationConfigState.id}
                    onIdpCreate={handleIdpCreate}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddIdentityProvider,
            renderFunction: renderModal
        });
    }

    const adminLoginSettingsResources = resources.pages.organizationDetails.tabs.adminLoginSettings;

    return (<div className={styles.organizationDetailsAdminLoginSettings}>
        <ContentPageHeader title={resources.pages.organizationDetails.tabs.adminLoginSettings.tabName} />
        <ContentPageBody isMaxWidth={true}>

            {!organizationConfigState.loginServiceIDPID ? (
                <div className={styles.adminLoginSettingsHeader}>
                    <span className={styles.description}>{adminLoginSettingsResources.noCorporateLoginMessage}</span>
                    {!isDisabled && (
                        <Button id="organization-details-create-corcoporate-login-button"
                            className={styles.button}
                            disabled={isDisabled}
                            onClick={handleConfirmCreateCorportateLogin}>
                            {adminLoginSettingsResources.inputs.createCorporateLogin.label}
                        </Button>
                    )}
                </div>
            ) : (
                <div>
                    <InputFormGroup
                        controlId="organization-details-admin-login-service-name-input"
                        automationId={"organization-details-admin-login-service-name-input"}
                        label={adminLoginSettingsResources.inputs.serviceName.label}
                        ariaLabel={adminLoginSettingsResources.inputs.serviceName.label}
                        value={serviceName}
                        disabled={true}
                        isRequired={true}
                        labelWidth={labelWidth}
                        onChange={() => { }}
                    />

                    <InputFormGroup
                        controlId="organization-details-admin-login-api-key-input"
                        automationId={"organization-details-admin-login-api-key-input"}
                        label={adminLoginSettingsResources.inputs.apiKey.label}
                        ariaLabel={adminLoginSettingsResources.inputs.apiKey.label}
                        value={serviceApiKey}
                        disabled={true}
                        isRequired={true}
                        labelWidth={labelWidth}
                        onChange={() => { }}
                    />

                    <SelectFormGroup
                        controlId={"organization-details-admin-login-idp-name-type"}
                        automationId={"organization-details-admin-login-idp-name-type"}
                        label={adminLoginSettingsResources.inputs.idpName.label}
                        ariaLabel={adminLoginSettingsResources.inputs.idpName.label}
                        disabled={isDisabled}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        labelWidth={labelWidth}
                        onChange={handleIdpChange}
                        options={axnIdentityProviderOptions}
                        selectedOption={selectedIdentityProviderOption}
                    />
                    {shouldShowIDPIDWarning &&
                        <Alert variant="warning">{adminLoginSettingsResources.updateUserCredentialsWarnigmessage}</Alert>
                    }
                    {organizationConfigState.permissions.canModifyLoginSettings && canCreateIdentityProviders && (
                        <div className={styles.flexEnd}>
                            <Button
                                id="organization-details-admin-login-setting-create-idp-button"
                                disabled={isDisabled}
                                variant="primary"
                                onClick={handleOpenCreateIDPModal}>
                                {adminLoginSettingsResources.buttons.createIDP.label}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </ContentPageBody>
    </div>);
}